import React, { useState, useRef, useEffect } from "react";
import { Sheet } from "react-modal-sheet";
import { PayOfflineButton, PayOnline, SheetHeader, SheetTitle } from "./StyledComponents";
import { toast } from "react-toastify";
import Config, { staff_roles } from "../Config";
import { MdKeyboardArrowRight } from "react-icons/md";
import { useOfflinePayment } from "../hooks/useOfflinePayment";
import { useVerifyOfflineOtp } from "../hooks/useVerifyOfflineOtp";

const CollectorsSheet = ({ isOpen, onClose, collectors, isLoading, onRetry, token, selectedDues, totalAmount, enableDueSelection }) => {
    const [processingCollectorId, setProcessingCollectorId] = useState(null);
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [selectedCollector, setSelectedCollector] = useState(null);
    const inputRefs = useRef([]);
    const { processOfflinePayment, isOfflinePaymentLoading } = useOfflinePayment(token);
    const { verifyOfflinePayment, isVerifyingOtp } = useVerifyOfflineOtp(token);
    const [otpError, setOtpError] = useState('');
    useEffect(() => {
        if (!isOpen) {
            setShowOtpInput(false);
            setOtp(['', '', '', '', '', '']);
            setSelectedCollector(null);
            setProcessingCollectorId(null);
            setOtpError('');
        }
    }, [isOpen]);

    const getRoleLabel = (collector) => {
        const { userType, role } = collector;
        if (userType === Config.USER_TYPE.CLIENT && role === Config.STAFF_ROLES.CLIENT) {
            return 'Owner';
        }

        if (userType === Config.USER_TYPE.STAFF && role !== Config.STAFF_ROLES.CLIENT) {
            const staffRole = staff_roles.find(r => r.value === role);
            return staffRole ? staffRole.label : 'Staff';
        }
        return 'Staff';
    };

    const getSelectedDueIds = () => {
        if (enableDueSelection) {
            return selectedDues.filter(due => due.selected).map(due => due.id);
        }
        return selectedDues.map(due => due.id);
    };

    const getSelectedLedgerReferenceIds = () => {
        if (enableDueSelection) {
            return selectedDues.filter(due => due.selected).map(due => due.ledgerReferenceId);
        }
        return selectedDues.map(due => due.ledgerReferenceId);
    };

    const getSelectedDuesList = () => {
        if (enableDueSelection) {
            return selectedDues.filter(due => due.selected);
        }
        return selectedDues;
    };

    const handleCollectorClick = async (collector) => {
        const selectedDueIds = getSelectedDueIds();

        if (selectedDueIds.length === 0) {
            toast.error("No dues selected for payment");
            return;
        }

        const payload = {
            dueIds: selectedDueIds,
            userType: collector.userType,
            userId: collector.userId,
            amount: totalAmount
        };

        setProcessingCollectorId(collector.userId);
        setSelectedCollector(collector);
        setOtpError('');

        await processOfflinePayment(payload, {
            onSuccess: (response) => {
                setProcessingCollectorId(null);
                if (response?.data?.isSuccess) {
                    setShowOtpInput(true);
                    setOtp(['', '', '', '', '', '']);
                    setTimeout(() => {
                        if (inputRefs.current[0]) {
                            inputRefs.current[0].focus();
                        }
                    }, 100);
                } else {

                    setTimeout(() => {
                        onClose();
                        setOtp(['', '', '', '', '', '']);
                        setShowOtpInput(false);
                        setSelectedCollector(null);
                    }, 1500);
                }
            },
            onError: () => {
                setProcessingCollectorId(null);
                setSelectedCollector(null);
            }
        });
    };

    const handleOtpChange = (index, value) => {
        if (value && !/^\d+$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setOtpError('');

        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleVerifyOtp = async () => {
        const otpString = otp.join('');

        if (otpString.length !== 6) {
            toast.error("Please enter complete 6-digit OTP");
            return;
        }

        const selectedDueIds = getSelectedDueIds();
        const selectedLedgerReferenceIds = getSelectedLedgerReferenceIds();

        const verifyPayload = {
            dueIds: selectedDueIds,
            otp: otpString,
            ledgerReferenceIds: selectedLedgerReferenceIds,
            userId: selectedCollector?.userId,
            userType: selectedCollector?.userType,
            amount: totalAmount
        };

        verifyOfflinePayment(verifyPayload, {
            onSuccess: () => {
                setTimeout(() => {
                    onClose();
                    setOtp(['', '', '', '', '', '']);
                    setShowOtpInput(false);
                    setSelectedCollector(null);
                    setOtpError('');
                }, 1500);
            },
            onError: (error) => {
                const errorMessage = error?.response?.data?.msg ||
                    "OTP verification failed";
                setOtpError(errorMessage);
            }
        });
    };

    const handleBackToCollectors = () => {
        setShowOtpInput(false);
        setOtp(['', '', '', '', '', '']);
        setSelectedCollector(null);
    };

    const isLoadingForCollector = (collectorId) => {
        return processingCollectorId === collectorId && isOfflinePaymentLoading;
    };

    const selectedDuesList = getSelectedDuesList();
    const totalPayableAmount = totalAmount;

    useEffect(() => {
        if (showOtpInput && inputRefs.current[0]) {
            setTimeout(() => {
                inputRefs.current[0].focus();
            }, 100);
        }
    }, [showOtpInput]);

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            className="mx-auto md:max-w-sm"
            detent="content-height"
            disableScrollLocking={true}
            overlayClassName="bg-neutral-900 backdrop-blur-sm bg-opacity-70"
        >
            <Sheet.Container>
                <SheetHeader className="relative">

                    <SheetTitle>{showOtpInput ? 'Verify Cash Payment' : 'Cash Payment'}</SheetTitle>
                </SheetHeader>
                <Sheet.Content>
                    <Sheet.Scroller className="overflow-y-auto p-4">
                        {!showOtpInput ? (
                            <>
                                {isLoading ? (
                                    <div className="flex flex-col justify-center items-center py-8 space-y-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                        <p className="text-gray-500">Loading collectors...</p>
                                    </div>
                                ) : collectors && collectors.length > 0 ? (
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 border-l-4 border-primary-color/80 p-3 mb-2 rounded">
                                            <p className="text-sm text-primary-color">
                                                Please select a collector to make your cash payment of <strong>₹{totalPayableAmount}</strong>.
                                                They will assist you with the payment process.
                                            </p>
                                        </div>

                                        {collectors.map((collector, index) => {
                                            const isProcessing = processingCollectorId !== null && isOfflinePaymentLoading;
                                            const isThisCollectorProcessing = processingCollectorId === collector.userId && isOfflinePaymentLoading;

                                            return (
                                                <div
                                                    key={collector.userId || index}
                                                    className={`bg-white rounded-lg p-4 border border-gray-200 cursor-pointer active:bg-gray-50 ${isProcessing
                                                        ? 'opacity-60 cursor-not-allowed pointer-events-none'
                                                        : 'hover:shadow-md'
                                                        }`}
                                                    onClick={() => !isProcessing && handleCollectorClick(collector)}
                                                >
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                            <span className="text-primary-color text-xl font-semibold">
                                                                {collector.name?.charAt(0) || "C"}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="font-semibold text-gray-800 text-base">
                                                                {collector.name || `Collector ${index + 1}`}
                                                            </h3>
                                                            <p className="text-xs text-gray-500">
                                                                {getRoleLabel(collector)}
                                                            </p>
                                                        </div>
                                                        <div className="ml-2">
                                                            {isThisCollectorProcessing ? (
                                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-color"></div>
                                                            ) : (
                                                                <MdKeyboardArrowRight className="text-gray-400 text-xl" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="text-6xl mb-4">👥</div>
                                        <p className="text-gray-500 font-medium">No collectors available</p>
                                        <p className="text-sm text-gray-400 mt-2">Please try again later or contact support.</p>
                                        {onRetry && (
                                            <button
                                                onClick={onRetry}
                                                className="mt-4 px-4 py-2 bg-primary-color text-white rounded-lg text-sm transition-colors"
                                            >
                                                Retry
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            /* OTP Verification Section */
                            <div className="space-y-6">
                                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded">
                                    <p className="text-sm text-yellow-700">
                                        An OTP has been sent to <strong>{selectedCollector?.name}'s</strong> registered mobile number.
                                        Please enter the 6-digit OTP to complete the payment.
                                    </p>
                                </div>

                                <div className="bg-white rounded-lg p-6 border border-gray-200">
                                    <div className="space-y-5">
                                        {/* OTP Input Boxes */}
                                        <div className="space-y-1.5">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Enter OTP
                                            </label>
                                            <div className="flex justify-center gap-2 sm:gap-2.5">
                                                {otp.map((digit, index) => (
                                                    <input
                                                        key={index}
                                                        ref={(el) => inputRefs.current[index] = el}
                                                        type="text"
                                                        inputMode="numeric"
                                                        maxLength={1}
                                                        value={digit}
                                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                        className="w-12 h-12 sm:w-14 sm:h-14 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-all"
                                                        autoFocus={index === 0 && showOtpInput}
                                                        autoComplete="off"
                                                    />

                                                ))}

                                            </div>
                                            {otpError && (
                                                <p className="text-rose-500 text-sm">{otpError}</p>
                                            )}
                                            {/* Resend OTP Button */}
                                            <div className="text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => handleCollectorClick(selectedCollector)}
                                                    className="text-sm text-gray-800 font-medium"
                                                >
                                                    Didn't Receive the OTP? <span className="text-primary-color"> {selectedCollector && isLoadingForCollector(selectedCollector?.userId) ? 'Sending OTP...' : 'Resend OTP'}</span>
                                                </button>
                                            </div>
                                        </div>



                                        {/* Action Buttons */}
                                        <div className="flex gap-3">
                                            <PayOfflineButton
                                                onClick={handleBackToCollectors}
                                            >
                                                Back
                                            </PayOfflineButton>
                                            <PayOnline
                                                onClick={handleVerifyOtp}
                                                disabled={otp.join('').length !== 6 || isVerifyingOtp}
                                                className={`${otp.join('').length === 6 && !isVerifyingOtp
                                                    ? 'bg-black text-white'
                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    }`}
                                            >
                                                {isVerifyingOtp ? (
                                                    <div className="flex items-center justify-center">
                                                        {/* <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> */}
                                                        Verifying...
                                                    </div>
                                                ) : (
                                                    'Verify & Pay'
                                                )}
                                            </PayOnline>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Sheet.Scroller>
                </Sheet.Content>
            </Sheet.Container >
        </Sheet >
    );
};

export default CollectorsSheet;